import { createBrowserRouter, redirect } from 'react-router';
import { AdminOnly, HrOrAdmin } from './RequireRole.js';
import { NotFound } from './NotFound.js';
import { RouteError } from './RouteError.js';

// The current portal's route tree (front-end inventory §1), path for path, so every link and
// deep link keeps resolving; every page in it is carried forward. Around the tree, CPv2 adds the
// session gate, the role guards (for usability; the server enforces them) and an error element
// that keeps the navigation when a page fails.

export const router = createBrowserRouter([
  { path: '/access-denied', lazy: () => import('./AccessDeniedPage.js') },
  {
    lazy: () => import('./RequireUser.js'),
    children: [
      {
        path: '/',
        lazy: () => import('./user/UserLayout.js'),
        children: [
          {
            ErrorBoundary: RouteError,
            children: [
              { index: true, lazy: () => import('./user/HomePage.js') },
              { path: 'training', lazy: () => import('./user/MyCampaignsPage.js') },
              { path: 'campaigns/:campaignId', lazy: () => import('./user/CampaignPlayerPage.js') },
              { path: 'audit-evidence', lazy: () => import('./user/AuditEvidencePage.js') },
              { path: 'audit-evidence/:controlId', lazy: () => import('./user/AuditEvidenceDetailPage.js') },
              { path: 'first-day', lazy: () => import('./user/FirstDayPage.js') },
              { path: 'incidents', lazy: () => import('./user/ReportIncidentPage.js') },
              { path: 'incidents/new', lazy: () => import('./user/NewIncidentPage.js') },
              { path: 'incidents/:incidentId', lazy: () => import('./user/IncidentDetailPage.js') },
              { path: 'vendors', lazy: () => import('./user/VendorManagementPage.js') },
              { path: 'policies', lazy: () => import('./user/MyPolicyApprovalsPage.js') },
              {
                path: 'policies/:policyId/versions/:versionId',
                lazy: () => import('./user/PolicyReviewPage.js'),
              },
              { path: '*', Component: NotFound },
            ],
          },
        ],
      },
      {
        path: '/admin',
        lazy: () => import('./admin/AdminLayout.js'),
        children: [
          {
            ErrorBoundary: RouteError,
            children: [
              {
                Component: AdminOnly,
                children: [
                  { index: true, lazy: () => import('./admin/DashboardPage.js') },
                  { path: 'users', lazy: () => import('./admin/UsersPage.js') },
                  { path: 'policies', lazy: () => import('./admin/PolicyManagementPage.js') },
                  { path: 'policies/:policyId', lazy: () => import('./admin/PolicyDetailPage.js') },
                  { path: 'vendors', lazy: () => import('./admin/VendorManagementPage.js') },
                  { path: 'vendors/:vendorId', lazy: () => import('./admin/VendorDetailPage.js') },
                  { path: 'incidents', lazy: () => import('./admin/IncidentManagementPage.js') },
                  { path: 'incidents/:incidentId', lazy: () => import('./admin/IncidentReviewPage.js') },
                  { path: 'modules', lazy: () => import('./admin/ModulesPage.js') },
                  { path: 'modules/new', lazy: () => import('./admin/NewModulePage.js') },
                  { path: 'modules/:moduleId', lazy: () => import('./admin/ModuleDetailPage.js') },
                  { path: 'modules/:moduleId/publish', lazy: () => import('./admin/PublishVersionPage.js') },
                  { path: 'campaigns', lazy: () => import('./admin/CampaignsPage.js') },
                  { path: 'campaigns/new', lazy: () => import('./admin/NewCampaignPage.js') },
                  { path: 'campaigns/:campaignId', lazy: () => import('./admin/CampaignDetailPage.js') },
                  { path: 'campaigns/:campaignId/edit', lazy: () => import('./admin/EditCampaignPage.js') },
                  { path: 'campaigns/:campaignId/assign', lazy: () => import('./admin/AssignUsersPage.js') },
                  { path: 'transfers', lazy: () => import('./admin/TransferManagementPage.js') },
                  { path: 'transfers/new', lazy: () => import('./admin/NewTransferPage.js') },
                  { path: 'transfers/:transferId', lazy: () => import('./admin/TransferDetailPage.js') },
                  {
                    path: 'audit-evidence',
                    lazy: () => import('./admin/AuditEvidenceLayout.js'),
                    children: [
                      { index: true, loader: () => redirect('/admin/audit-evidence/onboarding-transfers') },
                      { path: 'training', lazy: () => import('./admin/Kb4TrainingPage.js') },
                      { path: 'onboarding-transfers', lazy: () => import('./admin/AuditEvidencePage.js') },
                      { path: 'training/:kb4CampaignId', lazy: () => import('./admin/Kb4RosterSelectionPage.js') },
                      { path: 'control-evidence', lazy: () => import('./admin/HyperproofReviewPage.js') },
                      { path: 'master-list', lazy: () => import('./admin/HyperproofMasterListPage.js') },
                      { path: 'supplemental', lazy: () => import('./admin/HyperproofSupplementalPage.js') },
                    ],
                  },
                  { path: '*', Component: NotFound },
                ],
              },
              {
                path: 'onboarding',
                Component: HrOrAdmin,
                children: [
                  { index: true, lazy: () => import('./admin/OnboardingManagementPage.js') },
                  { path: 'new', lazy: () => import('./admin/NewOnboardingPage.js') },
                  { path: 'provision', lazy: () => import('./admin/NewHireProvisioningPage.js') },
                  { path: 'groups', lazy: () => import('./admin/OnboardingGroupsPage.js') },
                  { path: ':recordId', lazy: () => import('./admin/OnboardingDetailPage.js') },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/interviewer',
        lazy: () => import('./user/UserLayout.js'),
        children: [
          {
            ErrorBoundary: RouteError,
            children: [
              { path: 'onboarding/:recordId', lazy: () => import('./interviewer/InterviewerOnboardingPage.js') },
            ],
          },
        ],
      },
    ],
  },
]);
